import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMockStore } from '@/lib/stores/mockStore';
import { useBairros, type BairroView } from '@/lib/hooks/useBairros';
import { useTemas } from '@/lib/hooks/useTemas';
import { useEscala } from '@/lib/escala';
import { adminApi } from '@/lib/api/admin';
import { candidatosApi, type ResultadoZona } from '@/lib/api/candidatos';
import { votosOrfaosApi } from '@/lib/api/analise';
import { ModalTerritorio } from './ModalTerritorio';
import { bairrosApi } from '@/lib/api/bairros';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { solicitacoesApi } from '@/lib/api/solicitacoes';
import { acoesApi } from '@/lib/api/acoes';
import { emendasApi } from '@/lib/api/emendas';
import { contatosApi } from '@/lib/api/contatos';
import { liderancasApi } from '@/lib/api/liderancas';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

/**
 * Cargos do bloco de zonas e o ciclo em que cada um foi disputado.
 *
 * Antes daqui havia `ZONAS_MOCK`: tres zonas com nomes de candidatos e votos
 * escritos no codigo. A apuracao por zona ja existe na base -- `eleg.resultado_zona`,
 * carregada de `tse-import.ts` e exposta por `GET /eleg/candidatos/resultados`,
 * que aceita ciclo e cargo. O que a tela mostrava nao era uma previsao nem uma
 * estimativa: era um exemplo, e um exemplo ocupando o lugar da apuracao.
 *
 * O valor de `cargo` e o mesmo que a carga grava (`normalizarCargo`:
 * minusculas, sem acento, espaco vira `_`). O ciclo e o calendario eleitoral
 * brasileiro -- municipal em 2024, geral em 2022 -- e o rotulo o diz.
 */
const CARGOS_ZONA = [
  { id: 'vereador', cargo: 'vereador', ciclo: 2024, label: 'Vereador · 2024' },
  { id: 'prefeito', cargo: 'prefeito', ciclo: 2024, label: 'Prefeito · 2024' },
  { id: 'depest', cargo: 'deputado_estadual', ciclo: 2022, label: 'Dep. estadual · 2022' },
  { id: 'depfed', cargo: 'deputado_federal', ciclo: 2022, label: 'Dep. federal · 2022' },
] as const;

type IdCargoZona = (typeof CARGOS_ZONA)[number]['id'];

/**
 * A entidade `ResultadoZona` do servidor tem `uf` e `municipio` (e e por eles
 * que o escopo filtra), mas o tipo publicado em `lib/api/candidatos.ts` ainda
 * nao os declara. Declarar opcional aqui e tratar a ausencia e mais honesto do
 * que assumir que vem sempre preenchido: nas linhas semeadas em
 * desenvolvimento os dois campos ficam em branco.
 */
type LinhaZona = ResultadoZona & { uf?: string; municipio?: string };

const semAcento = (v: string) =>
  v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

const fmtN = (v: number) => v.toLocaleString('pt-BR');
const fmt1 = (v: number) => v.toFixed(1).replace('.', ',');

/**
 * Indicadores publicos do territorio, por assunto.
 *
 * Nao sao o vocabulario de tema -- esse vem de `useTemas()` (categorias de
 * `solicitacao` do proprio gabinete). Sao evidencia que a base ja tem sobre o
 * territorio, casada com o tema pelo assunto. Antes, esta tela cravava seis
 * nomes proprios de tema ('Saude basica', 'Creche e educacao'...) que nao tem
 * UM literal em comum com o catalogo real ('Saude e postos de atendimento',
 * 'Vagas em creches e educacao'...): a coluna "Solicitacoes" dava zero em toda
 * linha e a leitura estrategica acusava sempre falta de acao.
 *
 * Tema sem indicador correspondente nao ganha evidencia inventada: fica sem
 * evidencia publica, e o score se recalcula sobre os componentes que existem.
 */
const INDICADORES: Array<{
  casa: RegExp;
  ev: (b: BairroView) => string;
  pub: (b: BairroView) => number;
}> = [
  {
    casa: /saude|posto|ubs|medic/,
    ev: (b) =>
      `${b.ubs} UBS para ${fmtN(b.pop)} hab. (ideal: ${b.ubsIdeal}) · fila ${b.fila} dias`,
    pub: (b) => Math.min(100, Math.round((1 - b.ubs / (b.ubsIdeal || 1)) * 70 + b.fila)),
  },
  {
    casa: /ilumina|via|pavimenta|asfalt/,
    ev: (b) => `${100 - b.pav}% das vias sem pavimento (SNIS) · ${b.escuros} pontos escuros`,
    pub: (b) => Math.min(100, (100 - b.pav) * 2 + Math.round(b.escuros / 3)),
  },
  {
    casa: /creche|educa|escola/,
    ev: (b) => `Déficit de ${fmtN(b.creche)} vagas (INEP · 0–3 anos)`,
    pub: (b) => Math.min(100, Math.round(b.creche / 12)),
  },
  {
    casa: /saneamento|esgoto|drenagem|agua/,
    ev: (b) => `${b.esgoto}% com esgoto coletado (SNIS)`,
    pub: (b) => 100 - b.esgoto,
  },
  {
    casa: /assist|social|cadunico|cadastro unico/,
    ev: (b) => `${b.cad}% das famílias no CadÚnico`,
    pub: (b) => Math.min(100, b.cad * 2),
  },
  {
    casa: /lazer|esporte|praca|quadra/,
    ev: (b) => `${b.pracas} praça(s) equipada(s) para ${fmtN(b.pop)} hab.`,
    pub: (b) => Math.max(0, 70 - b.pracas * 9),
  },
];

export function TerrPage() {
  //  é seleção de interface; o resto vem do banco.
  const bairroActiveId = useMockStore((s) => s.bairroActiveId);
  const setBairroActiveId = useMockStore((s) => s.setBairroActiveId);
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const { data: solicitacoes = [] } = useQuery({
    queryKey: ['solicitacoes', {}],
    queryFn: () => solicitacoesApi.list({}, token),
    enabled: !!token,
  });
  const { data: acoes = [] } = useQuery({
    queryKey: ['acoes', {}],
    queryFn: () => acoesApi.list({}, token),
    enabled: !!token,
  });
  const { data: emendas = [] } = useQuery({
    queryKey: ['emendas'],
    queryFn: () => emendasApi.list(token),
    enabled: !!token,
  });
  const { data: contatosPagina } = useQuery({
    queryKey: ['contatos', 'territorio'],
    queryFn: () => contatosApi.list({ limit: 500 }, token),
    enabled: !!token,
  });
  const contatos = contatosPagina?.items ?? [];
  const { data: liderancas = [] } = useQuery({
    queryKey: ['liderancas', {}],
    queryFn: () => liderancasApi.list({}, token),
    enabled: !!token,
  });

  // Vocabulário de tema: fonte única do gabinete, não uma lista desta tela.
  const { temas } = useTemas();

  // Nome, município e UF do mandato. `useEscala` já consulta `['mandato']`;
  // a mesma chave reaproveita o cache em vez de pedir de novo.
  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
    staleTime: 60_000,
  });

  // Estado das abas antes das consultas que dependem dele.
  const [escopoNivel, setEscopoNivel] = useState<'bairro' | 'mun' | 'uf'>('bairro');
  const [activeTab, setActiveTab] = useState<'geral' | 'zonas' | 'midias' | 'orfaos' | 'agentes'>('geral');
  const [cargoZona, setCargoZona] = useState<IdCargoZona>('vereador');
  const [zonaAberta, setZonaAberta] = useState<string | null>(null);
  const [metaVotos, setMetaVotos] = useState<number>(10000);

  const cargoDef = CARGOS_ZONA.find((c) => c.id === cargoZona) ?? CARGOS_ZONA[0];

  // Apuração por zona do TSE. Só é buscada na aba que a mostra: são milhares
  // de linhas por ciclo e ninguém as vê na visão geral.
  const { data: linhasZona = [], isLoading: carregandoZonas } = useQuery({
    queryKey: ['resultados-zona', cargoDef.ciclo, cargoDef.cargo],
    queryFn: () =>
      candidatosApi.resultadosPorZona(cargoDef.ciclo, token, cargoDef.cargo) as Promise<LinhaZona[]>,
    enabled: !!token && activeTab === 'zonas',
  });
  const { data: candidatosBase = [] } = useQuery({
    queryKey: ['candidatos', {}],
    queryFn: () => candidatosApi.list(token),
    enabled: !!token && activeTab === 'zonas',
  });

  // "Votos em aberto": quem o gabinete registrou como fora da disputa. É
  // cadastro do próprio mandato (`eleg.voto_orfao`, tipo `desistente`), a mesma
  // base da tela de Votos órfãos — antes daqui havia cinco nomes de candidatos
  // escritos no código, com fontes e datas igualmente escritas no código.
  const { data: orfaos = [] } = useQuery({
    queryKey: ['votos-orfaos', cargoDef.ciclo],
    queryFn: () => votosOrfaosApi.list(token, cargoDef.ciclo),
    enabled: !!token && activeTab === 'zonas',
  });

  const criarAcao = useMutation({
    mutationFn: (dados: any) => acoesApi.create(dados, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acoes'] });
      queryClient.invalidateQueries({ queryKey: ['matriz'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast('Ação planejada e registrada no território.');
      setModalNovaAcao(false);
      setNovaAcaoTitulo('');
    },
    onError: (err: any) => {
      const m = err?.response?.data?.message;
      toast(Array.isArray(m) ? m.join(' · ') : m || 'Erro ao criar a ação.');
    },
  });
  const { bairros, isLoading: carregandoBairros } = useBairros();

  // A API recusa excluir território com contatos vinculados (409) para não
  // deixar registros apontando para um nome que não existe mais.
  const excluirTerritorio = useMutation({
    mutationFn: (bairroId: string) => bairrosApi.delete(bairroId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bairros'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['metas'] });
      queryClient.invalidateQueries({ queryKey: ['matriz'] });
      setBairroActiveId('');
      toast('Território removido.');
    },
    onError: (err: any) => {
      const m = err?.response?.data?.message;
      toast(Array.isArray(m) ? m.join(' · ') : m || 'Erro ao remover o território.');
    },
  });
  // Rotulo da unidade territorial segue a escala do mandato (bairro, municipio, estado...)
  const { unidade } = useEscala();

  // Estados de Chat com IA
  const [estrategistaAtivo, setEstrategistaAtivo] = useState('O Cartógrafo');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ q: string; ag: string; r: { f: string; c: string; i: string; fontes: string } }>>([
    {
      q: 'Como está Messejana? (resumo)',
      ag: 'O Cartógrafo',
      r: {
        f: 'Messejana: 42.318 moradores, 31.207 eleitores, 1.548 votos em 2024 (18,4% da candidata), 120 contatos cadastrados, 3 lideranças ativas e 18 solicitações registradas.',
        c: 'Taxa de retorno de demandas: 84%. Índice de necessidade: 78/100 (alta prioridade em Saúde e Iluminação).',
        i: 'Base eleitoral mais forte e consolidada. O foco deve ser a entrega e a comunicação contínua dos marcos da UBS e das obras de iluminação.',
        fontes: 'IBGE Censo 2022 · TSE 2024 · Gabinete360 · Perfil Territorial',
      },
    },
  ]);

  // Modais
  const [modalNovaAcao, setModalNovaAcao] = useState(false);
  const [modalEditarTerr, setModalEditarTerr] = useState(false);
  const [modalNovoTerr, setModalNovoTerr] = useState(false);
  const [novaAcaoTitulo, setNovaAcaoTitulo] = useState('');
  const [novaAcaoTipo, setNovaAcaoTipo] = useState('Obra');
  // Vazio, não 'Saúde básica': o tema padrão é o primeiro do catálogo do
  // gabinete, e antes do catálogo carregar não há tema nenhum a assumir.
  const [novaAcaoTema, setNovaAcaoTema] = useState('');

  const currentBairro: BairroView | undefined =
    bairros.find((b) => b.id === bairroActiveId) || bairros[0];

  const handleExcluirTerritorio = () => {
    if (!currentBairro) return;
    const ok = window.confirm(
      `Excluir o território "${currentBairro.n}"?\n\nOs indicadores e as metas deste território são perdidos. Se houver contatos vinculados, a exclusão é recusada.`,
    );
    if (ok) excluirTerritorio.mutate(currentBairro.bairroId);
  };

  // Território vem da API: enquanto carrega (ou se o mandato ainda não tem
  // nenhum cadastrado) não há o que perfilar.
  // Sem território o mandato não tem por onde começar: Metas, Matriz, Campanhas
  // e Votação dependem daqui. Por isso o vazio oferece o cadastro em vez de só
  // informar a ausência.
  if (!currentBairro) {
    return (
      <div className="page" id="p-territorio">
        <div className="head">
          <div>
            <h1 style={{ color: 'var(--eleg)' }}>Perfil do território</h1>
            <p>
              O que este lugar é, o que ele pede e o que o mandato já fez por ele. Os indicadores
              públicos são os que constam da ficha do território. Sempre agregado, nunca por
              pessoa.
            </p>
          </div>
          {!carregandoBairros && (
            <div className="act">
              <button className="btn pri" onClick={() => setModalNovoTerr(true)}>
                + Cadastrar território
              </button>
            </div>
          )}
        </div>

        <div className="empty">
          {carregandoBairros
            ? 'Carregando territórios do mandato...'
            : 'Nenhum território cadastrado ainda. Cadastre o primeiro para liberar as metas, a matriz de performance e a segmentação de campanhas.'}
        </div>

        {modalNovoTerr && <ModalTerritorio onFechar={() => setModalNovoTerr(false)} />}
      </div>
    );
  }

  // Os módulos gravam o nome do território; a comparação é normalizada.
  const doTerritorio = (valor?: string) =>
    (valor ?? '').toLowerCase() === currentBairro.n.toLowerCase();

  /**
   * Uma linha por tema do catálogo do gabinete (`useTemas`), com a evidência
   * pública que a base tem sobre este território quando existe indicador para
   * o assunto.
   *
   * O score pondera só os componentes disponíveis: um tema sem indicador
   * público não recebe evidência de enchimento, e o peso dele é redistribuído
   * entre demanda e cobertura em vez de virar zero — zero afirmaria que o
   * indicador existe e é péssimo.
   */
  const rows = temas
    .map((t) => {
      const ind = INDICADORES.find((i) => i.casa.test(semAcento(t)));
      const dem = solicitacoes.filter((s) => doTerritorio(s.bairro) && s.tema === t).length;
      const cob = acoes.filter((a) => doTerritorio(a.bairro) && a.tema === t).length;
      const pub = ind ? ind.pub(currentBairro) : null;

      const componentes: Array<[number, number]> = [
        [0.4, Math.min(100, dem * 8)],
        [0.2, Math.max(0, 100 - cob * 50)],
      ];
      if (pub !== null) componentes.push([0.4, pub]);
      const pesos = componentes.reduce((a, [p]) => a + p, 0);
      const score = Math.round(componentes.reduce((a, [p, v]) => a + p * v, 0) / pesos);

      return { t, ev: ind ? ind.ev(currentBairro) : null, pub, dem, cob, score };
    })
    .sort((a, b) => b.score - a.score);

  // Sem linha não há tema no topo. O valor que estava aqui antes
  // (`{ t: 'Saúde básica', score: 80 }`) era dado de demonstração disfarçado de
  // padrão: com o catálogo vazio, a tela afirmava uma pressão que ninguém mediu.
  const top = rows[0];
  const semCob = rows.find((r) => r.cob === 0 && r.score > 40);

  // Cobertura do mandato neste território
  const cts = contatos.filter((c) => doTerritorio(c.bairro));
  const opt = cts.filter((c) => c.base_legal === 'optin').length;
  const lids = liderancas.filter((l) => doTerritorio(l.bairro) && l.status === 'ativa');
  const ac = acoes.filter((a) => doTerritorio(a.bairro));
  const em = emendas.filter((e) => doTerritorio(e.bairro));
  const sol = solicitacoes.filter((s) => doTerritorio(s.bairro));

  // Taxa de retorno: só conta o que saiu da fila. Sem solicitações, não há
  // taxa a exibir — antes assumia 80% quando não havia base de cálculo.
  const ret = sol.length
    ? Math.round(
        (sol.filter((s) => s.status === 'concluida' || s.status === 'encaminhada').length /
          sol.length) *
          100,
      )
    : null;

  // Total consolidado pelo servidor, sem recalcular estágios aqui
  const totalEmendasValor = em.reduce((acc, e) => acc + (e.total_indicado || 0), 0);

  // Cálculo de Votos Órfãos (brancos, nulos e abstenções projetados)
  const pop2026 = Math.round(currentBairro.pop * 1.04);
  const eleit2026 = Math.round(currentBairro.eleit * 1.04);
  const bnPct = 8.4;
  const abstPct = 20.8;
  const votosOrfaosTotais = Math.round((eleit2026 * (bnPct + abstPct)) / 100);

  // Primeiro tema do catálogo do gabinete enquanto ninguém escolher outro.
  const temaAcao = novaAcaoTema || temas[0] || '';

  const handleSalvarNovaAcao = () => {
    if (!novaAcaoTitulo.trim()) {
      toast('Informe o título da ação.');
      return;
    }
    if (!temaAcao) {
      toast('Cadastre ao menos uma categoria de solicitação em Configurações › Tipos.');
      return;
    }
    criarAcao.mutate({
      titulo: novaAcaoTitulo.trim(),
      tipo: novaAcaoTipo,
      bairro: currentBairro.n,
      // Do cadastro do território, não de 'Fortaleza'/'CE' cravados: o mandato
      // que não é de Fortaleza gravava ações na cidade errada.
      municipio: currentBairro.municipio,
      uf: currentBairro.uf,
      tema: temaAcao,
      status: 'planejada',
      progresso: 0,
      // Quantas solicitações abertas deste tema a ação passa a endereçar
      solicitacoes_resolvidas: sol.filter(
        (s) => s.tema === temaAcao && s.status !== 'concluida',
      ).length,
      // `alcance` NÃO vai daqui. O que havia era `pop * 0.1` — dez por cento da
      // população, sem nada na base que dissesse dez. Alcance é medição, e a
      // plataforma não mede alcance de ação: quem souber o número o digita em
      // Ações, onde o campo existe e o rótulo já diz que não é verificado.
      marco: 'Definir primeiro marco e licitação',
    });
  };

  const handleEnviarPerguntaChat = (textoPergunta?: string) => {
    const q = textoPergunta || chatInput;
    if (!q.trim()) return;

    let resp = {
      f: `${currentBairro.n} possui ${currentBairro.pop.toLocaleString('pt-BR')} moradores e ${currentBairro.votos.toLocaleString('pt-BR')} votos registrados.`,
      c: `A taxa de conversão é de ${(currentBairro.pct).toFixed(1)}% e há ${sol.length} solicitações abertas.`,
      i: top
        ? `Recomenda-se focar na resolução da demanda prioritária de ${top.t.toLowerCase()} e manter reuniões quinzenais com as ${lids.length} lideranças ativas.`
        : `Sem categorias de solicitação cadastradas não há tema a priorizar; mantenha as reuniões quinzenais com as ${lids.length} lideranças ativas.`,
      fontes: 'IBGE 2022 · TSE 2024 · Gabinete360',
    };

    if (top && (q.toLowerCase().includes('precisa') || q.toLowerCase().includes('prioridade'))) {
      resp = {
        f: `A principal demanda registrada no gabinete é ${top.t} com ${top.dem} pedidos nos últimos 90 dias e evidência de necessidade ${top.score}/100.`,
        c: `Representa ${Math.round((top.dem / (sol.length || 1)) * 100)}% de todas as solicitações do bairro.`,
        i: `Criar uma emenda parlamentar ou indicação de obra civil com marco de 30 dias para entrega aos moradores.`,
        fontes: 'Módulo Gabinete · Solicitações · Ouvidoria',
      };
    } else if (q.toLowerCase().includes('meta') || q.toLowerCase().includes('voto')) {
      resp = {
        f: `A meta projetada para ${currentBairro.n} em 2028 é de ${Math.round(currentBairro.votos * 1.35).toLocaleString('pt-BR')} votos (+35%).`,
        c: `Existem ${votosOrfaosTotais.toLocaleString('pt-BR')} votos órfãos (brancos, nulos e abstenções) no território.`,
        i: `Convertendo apenas 12% dos votos órfãos (~${Math.round(votosOrfaosTotais * 0.12).toLocaleString('pt-BR')} votos), a meta eleitoral de 2028 é superada.`,
        fontes: 'TSE 2024 · Projeção Eleg · Votos Órfãos',
      };
    }

    setChatMessages((prev) => [
      ...prev,
      {
        q,
        ag: estrategistaAtivo,
        r: resp,
      },
    ]);
    setChatInput('');
  };

  // Município e UF do escopo vêm do cadastro, não de 'Fortaleza'/'Ceará'
  // escritos no código — a tela servia a um mandato só.
  const municipioEscopo = mandato?.cidade || currentBairro.municipio;
  const ufEscopo = (mandato?.uf || currentBairro.uf || '').toUpperCase();

  const scopeLabel =
    escopoNivel === 'uf'
      ? `${ufEscopo} (estado)`
      : escopoNivel === 'mun'
      ? `${municipioEscopo} (município)`
      : currentBairro.n;

  const nomeMandato = mandato?.nome || '';

  // ---- Zonas eleitorais: apuração do TSE, recortada pelo escopo ----------
  //
  // O recorte do escopo é o do protótipo (`zonasEscopo()`, proto:1816), com uma
  // diferença que a base impõe: lá a zona trazia a lista de bairros que ela
  // cobre; aqui não existe correspondência zona↔bairro em lugar nenhum —
  // `eleg.resultado_zona` tem UF, município, zona e seção, e `terr.bairro` não
  // tem zona. Então o escopo de território não consegue estreitar mais que o
  // município, e a tela diz isso em vez de fingir um recorte.
  const nomePorCandidato = new Map(candidatosBase.map((c) => [c.id, c.nome]));

  const linhasEscopo = linhasZona.filter((l) => {
    if (escopoNivel === 'uf') {
      return !l.uf || !ufEscopo || l.uf.toUpperCase() === ufEscopo;
    }
    return !l.municipio || semAcento(l.municipio) === semAcento(municipioEscopo);
  });

  const porZona = new Map<string, Map<string, number>>();
  for (const l of linhasEscopo) {
    if (!porZona.has(l.zona)) porZona.set(l.zona, new Map());
    const m = porZona.get(l.zona)!;
    m.set(l.candidato_id, (m.get(l.candidato_id) || 0) + l.votos);
  }

  const zonas = [...porZona.entries()]
    .map(([zona, votosPorCand]) => {
      const total = [...votosPorCand.values()].reduce((a, v) => a + v, 0);
      const candidatos = [...votosPorCand.entries()]
        .map(([id, votos]) => ({
          id,
          nome: nomePorCandidato.get(id) || id,
          votos,
          // Percentual DENTRO da zona: é o que a posição na zona significa.
          pct: total > 0 ? (votos / total) * 100 : 0,
        }))
        .sort((a, b) => b.votos - a.votos)
        .map((c, i) => ({ ...c, pos: i + 1 }));
      return { zona, total, candidatos };
    })
    .sort((a, b) => a.zona.localeCompare(b.zona));

  const totalVotosZonas = zonas.reduce((a, z) => a + z.total, 0);

  // Posição do mandato em cada zona onde ele aparece. Zona onde não aparece não
  // entra na média: contá-la como último lugar inventaria uma posição.
  const posicoesMandato = zonas
    .map((z) => ({
      zona: z.zona,
      pos: z.candidatos.find((c) => semAcento(c.nome) === semAcento(nomeMandato))?.pos ?? null,
      votos: z.candidatos.find((c) => semAcento(c.nome) === semAcento(nomeMandato))?.votos ?? 0,
    }))
    .filter((p): p is { zona: string; pos: number; votos: number } => p.pos !== null);

  const mediaPos = posicoesMandato.length
    ? posicoesMandato.reduce((a, p) => a + p.pos, 0) / posicoesMandato.length
    : null;
  const votosMandatoZonas = posicoesMandato.reduce((a, p) => a + p.votos, 0);
  const maisForte = [...posicoesMandato].sort((a, b) => a.pos - b.pos)[0];
  const maisFraca = [...posicoesMandato].sort((a, b) => b.pos - a.pos)[0];

  const lideres = new Map<string, number>();
  for (const z of zonas) {
    const l = z.candidatos[0];
    if (l) lideres.set(l.nome, (lideres.get(l.nome) || 0) + 1);
  }
  const topLider = [...lideres.entries()].sort((a, b) => b[1] - a[1])[0];

  // Comparativo candidato × zona: os seis maiores do escopo, mais o mandato
  // quando ele ficar de fora (proto:1819).
  const totaisPorCand = new Map<string, number>();
  for (const z of zonas) {
    for (const c of z.candidatos) {
      totaisPorCand.set(c.nome, (totaisPorCand.get(c.nome) || 0) + c.votos);
    }
  }
  const nomesComparativo = [...totaisPorCand.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([n]) => n);
  if (nomeMandato && totaisPorCand.has(nomeMandato) && !nomesComparativo.includes(nomeMandato)) {
    nomesComparativo.push(nomeMandato);
  }

  // Saídas da disputa: cadastro do gabinete, tipo `desistente`.
  const saidas = orfaos.filter((o) => o.tipo === 'desistente');
  const votosEmAberto = saidas.reduce((a, o) => a + (o.votos || 0), 0);

  return (
    <div className="page" id="p-terr">
      {/* Cabeçalho */}
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>Perfil do território · {scopeLabel}</h1>
          <p>
            {/*
              proto:708 lista "IBGE, CNES, INEP, SNIS, CadÚnico agregado"
              como dados cruzados. Só IBGE e TSE têm importador de verdade
              (`terr/ibge/ibge-import.ts`, `eleg/tse/tse-import.ts`); UBS,
              pavimentação, fila de creche, esgoto e cobertura do CadÚnico são
              colunas de `terr.bairro` preenchidas na ficha do território. A
              tese final do protótipo — "sempre agregado, nunca por pessoa" —
              vale e volta.
            */}
            O que este lugar é, o que ele pede e o que o mandato já fez por ele. Os indicadores
            públicos são os que constam da ficha do território. Sempre agregado, nunca por pessoa.
          </p>
        </div>
        <div className="act">
          <button className="btn" onClick={() => setModalNovoTerr(true)}>
            + Novo território
          </button>
          <button className="btn" onClick={() => setModalEditarTerr(true)}>
            ✏️ Editar território
          </button>
          <button
            className="btn"
            onClick={handleExcluirTerritorio}
            disabled={excluirTerritorio.isPending}
            title={`Excluir ${currentBairro.n}`}
          >
            🗑️
          </button>
          <button className="btn pri" onClick={() => setModalNovaAcao(true)}>
            Planejar ação
          </button>
        </div>
      </div>

      {/* Barra de Escopo */}
      <div className="filters" style={{ margin: '-6px 0 14px' }}>
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Escopo da análise:</span>
        <span
          className={`chip eleg chipbtn ${escopoNivel === 'uf' ? 'on' : ''}`}
          onClick={() => setEscopoNivel('uf')}
        >
          Estado · {ufEscopo}
        </span>
        <span
          className={`chip eleg chipbtn ${escopoNivel === 'mun' ? 'on' : ''}`}
          onClick={() => setEscopoNivel('mun')}
        >
          Município · {municipioEscopo}
        </span>
        <span
          className={`chip eleg chipbtn ${escopoNivel === 'bairro' ? 'on' : ''}`}
          onClick={() => setEscopoNivel('bairro')}
        >
          {unidade}
        </span>

        {escopoNivel === 'bairro' && (
          <select
            className="sel"
            value={currentBairro.id}
            onChange={(e) => setBairroActiveId(e.target.value)}
          >
            {bairros.map((b) => (
              <option key={b.id} value={b.id}>
                {b.n}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* ABAS DO PERFIL DO TERRITÓRIO (Conforme Protótipo Oficial) */}
      <div className="tabs" style={{ margin: '0 0 16px' }}>
        <button
          className={activeTab === 'geral' ? 'on' : ''}
          onClick={() => setActiveTab('geral')}
        >
          Visão geral
        </button>
        <button
          className={activeTab === 'zonas' ? 'on' : ''}
          onClick={() => setActiveTab('zonas')}
        >
          Zonas eleitorais
        </button>
        <button
          className={activeTab === 'midias' ? 'on' : ''}
          onClick={() => setActiveTab('midias')}
        >
          Mídias e sentimento
        </button>
        <button
          className={activeTab === 'orfaos' ? 'on' : ''}
          onClick={() => setActiveTab('orfaos')}
        >
          Votos órfãos
        </button>
        <button
          className={activeTab === 'agentes' ? 'on' : ''}
          onClick={() => setActiveTab('agentes')}
        >
          Chat com agentes
        </button>
      </div>

      {/* ABA 1: VISÃO GERAL */}
      {activeTab === 'geral' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 4 KPIs Superiores */}
          <div className="grid g4">
            <div className="card kpi">
              <div className="l">Moradores</div>
              <div className="v">{currentBairro.pop.toLocaleString('pt-BR')}</div>
              <div className="d">Censo IBGE 2022 · {currentBairro.dom.toLocaleString('pt-BR')} domicílios</div>
            </div>

            <div className="card kpi">
              <div className="l">Eleitores</div>
              <div className="v">{currentBairro.eleit.toLocaleString('pt-BR')}</div>
              <div className="d">TSE 2024 · {((currentBairro.eleit / currentBairro.pop) * 100).toFixed(0)}% da população</div>
            </div>

            <div className="card kpi">
              <div className="l">Votos obtidos · 2024</div>
              <div className="v" style={{ color: 'var(--good)' }}>
                {currentBairro.votos.toLocaleString('pt-BR')}
              </div>
              <div className="d">
                {currentBairro.pct}% da votação · {((currentBairro.votos / currentBairro.eleit) * 100).toFixed(1)}% dos eleitores
              </div>
            </div>

            <div className="card kpi">
              <div className="l">Lideranças e contatos</div>
              <div className="v">
                {lids.length} <span style={{ fontSize: 14, color: 'var(--ink3)' }}>· {cts.length}</span>
              </div>
              <div className="d">{lids.length} liderança(s) ativa(s) · {opt} contatos com opt-in</div>
            </div>
          </div>

          {/* Grid Principal: Demandas vs Cobertura */}
          <div className="grid g21">
            <div className="card">
              <h2>
                O que {currentBairro.n} está pedindo{' '}
                <Info titulo="Como o score de pressão é calculado">
                  Score = demanda no gabinete (40%) + indicador público (40%) + ausência de
                  cobertura (20%). Tema sem indicador público mapeado é pontuado só pelos dois
                  componentes que existem, com os pesos redistribuídos: ele não entra com
                  indicador zero.
                </Info>
              </h2>

              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th>Tema</th>
                      <th>
                        Evidência pública{' '}
                        <Info titulo="Por que não há coluna de pesquisa">
                          As pesquisas cadastradas medem intenção de voto por candidato, não
                          quanto cada tema é citado no território.
                        </Info>
                      </th>
                      {/* A consulta não filtra por data: o rótulo "90d" afirmava
                          uma janela que o código não aplica. */}
                      <th>Solicitações registradas</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.t} className="row">
                        <td>
                          <Link to="/solicitacoes">
                            <b>{r.t}</b>
                          </Link>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {r.ev ?? (
                            <span style={{ color: 'var(--ink3)' }}>
                              sem indicador público mapeado para este tema
                            </span>
                          )}
                        </td>
                        <td className="num">{r.dem}</td>
                        <td className="num">
                          {r.cob > 0 ? (
                            <span className="chip good">{r.cob} ação</span>
                          ) : (
                            <span className="chip warn">0 ações</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {rows.length === 0 && (
                <div className="empty">
                  Nenhuma categoria de solicitação cadastrada. O vocabulário de tema é o do
                  gabinete: cadastre em Configurações › Tipos para esta leitura existir.
                </div>
              )}

              {/* A coluna "Pesquisa (% citaram)" saiu daqui. Os números eram
                  `bairro === 'messejana' ? 31 : 24` — escritos no código. As
                  pesquisas que a base guarda (`eleg.pesquisa`) registram intenção
                  de voto por candidato, não citação de tema por território: não
                  há de onde tirar essa coluna. */}

              {/* Leitura do Cartógrafo IA */}
              {top && (
                <div className="note" style={{ marginTop: 14, borderColor: 'var(--eleg)', background: 'var(--eleg-soft)' }}>
                  <b>Leitura do Cartógrafo (IA): </b>
                  {currentBairro.n} tem maior pressão em <b>{top.t.toLowerCase()}</b> ({top.dem} solicitações, {top.cob} ação do mandato).{' '}
                  {semCob ? (
                    <>
                      Não há nenhuma ação cobrindo <b>{semCob.t.toLowerCase()}</b>, onde a evidência pública é forte. Sugestão: indicação ou emenda parlamentar antes do próximo ciclo orçamentário.
                    </>
                  ) : (
                    <>Todos os temas críticos possuem ao menos uma ação em andamento; acompanhe os marcos e prazos.</>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <button className="btn sm pri" onClick={() => setModalNovaAcao(true)}>
                      Criar ação a partir desta leitura
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Coluna Lateral: Cobertura & Demografia */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="card">
                <h2>Cobertura do mandato aqui</h2>
                <dl className="dl">
                  <dt>Ações</dt>
                  <dd>
                    {ac.filter((a) => a.status === 'concluida').length} concluídas ·{' '}
                    {ac.filter((a) => a.status !== 'concluida' && a.status !== 'cancelada').length} em
                    curso
                  </dd>
                  <dt>Emendas</dt>
                  <dd className="num">R$ {totalEmendasValor.toLocaleString('pt-BR')}</dd>
                  <dt>Contatos</dt>
                  <dd className="num">{cts.length} · {opt} opt-in</dd>
                  <dt>Votos 2024</dt>
                  <dd className="num">{currentBairro.votos.toLocaleString('pt-BR')} ({currentBairro.pct}%)</dd>
                  <dt>Solicitações</dt>
                  <dd className="num">{sol.length}</dd>
                </dl>

                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span>Demandas com retorno ao cidadão</span>
                    <b className="num">{ret === null ? 'sem base' : `${ret}%`}</b>
                  </div>
                  <div className="bar">
                    <i style={{ width: `${ret ?? 0}%`, background: 'var(--good)' }} />
                  </div>
                  {ret === null && (
                    <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                      Nenhuma solicitação registrada neste território ainda.
                    </small>
                  )}
                </div>
              </div>

              <div className="card">
                <h2>Perfil etário e populacional</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                  {[
                    { faixa: '0–14 anos', pct: 22, v: Math.round(currentBairro.pop * 0.22) },
                    { faixa: '15–29 anos', pct: 26, v: Math.round(currentBairro.pop * 0.26) },
                    { faixa: '30–59 anos', pct: 38, v: Math.round(currentBairro.pop * 0.38) },
                    { faixa: '60+ anos', pct: 14, v: Math.round(currentBairro.pop * 0.14) },
                  ].map((f) => (
                    <div key={f.faixa}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span>{f.faixa}</span>
                        <span className="num">{f.v.toLocaleString('pt-BR')} ({f.pct}%)</span>
                      </div>
                      <div className="bar" style={{ height: 6 }}>
                        <i style={{ width: `${f.pct * 2}%`, background: 'var(--good)' }} />
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 8 }}>
                  {currentBairro.cad > 25 ? '61% dos domicílios chefiados por mulheres · População jovem acima da média.' : 'Perfil demográfico próximo da média de Fortaleza.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ABA 2: ZONAS ELEITORAIS (proto:1815-1833, zonasBloco) */}
      {activeTab === 'zonas' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ borderColor: 'var(--eleg)' }}>
            <h2 style={{ color: 'var(--eleg)' }}>Zonas eleitorais · {scopeLabel}</h2>
            <p style={{ color: 'var(--ink3)', fontSize: 13, margin: '-4px 0 10px' }}>
              Para cada zona, os candidatos mais votados no cargo escolhido. Apuração do TSE por
              zona, carregada em Ingestão TSE (<span className="mono">eleg.resultado_zona</span>).
            </p>

            {/* Seletor de Cargo */}
            <div className="filters" style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Cargo:</span>
              {CARGOS_ZONA.map((c) => (
                <span
                  key={c.id}
                  className={`chip eleg chipbtn ${cargoZona === c.id ? 'on' : ''}`}
                  onClick={() => {
                    setCargoZona(c.id);
                    setZonaAberta(null);
                  }}
                >
                  {c.label}
                </span>
              ))}
            </div>

            {escopoNivel === 'bairro' && (
              <div className="note" style={{ marginBottom: 12 }}>
                A apuração do TSE é por zona eleitoral, e não há correspondência zona↔bairro na
                base. Por isso o recorte de {unidade.toLowerCase()} não estreita a lista:{' '}
                <b>{`o que está abaixo são as ${zonas.length} zonas de ${municipioEscopo}`}</b>, e
                não as de {currentBairro.n}.{' '}
                <Info titulo="Por que a zona não se cruza com o território">
                  <span className="mono">eleg.resultado_zona</span> guarda UF, município, zona e
                  seção, e o cadastro de território não guarda zona.
                </Info>
              </div>
            )}

            {carregandoZonas && <div className="empty">Carregando a apuração por zona...</div>}

            {!carregandoZonas && zonas.length === 0 && (
              <div className="empty">
                Nenhum resultado por zona carregado para este escopo no ciclo {cargoDef.ciclo}.
                <div style={{ marginTop: 6 }}>
                  <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                    A apuração por zona vem da carga do TSE (<b>Ingestão TSE</b>,{' '}
                    <span className="mono">eleg.resultado_zona</span>). Enquanto a carga deste
                    ciclo e cargo não rodar, não há o que comparar, e nenhum número é estimado
                    para ocupar o lugar dela.
                  </small>
                </div>
              </div>
            )}

            {!carregandoZonas && zonas.length > 0 && (
              <>
                {/* 4 KPIs (proto:1820) */}
                <div className="grid g4">
                  <div className="card kpi">
                    <div className="l">Zonas no escopo</div>
                    <div className="v">{zonas.length}</div>
                    <div className="d">{zonas.map((z) => z.zona).join(' · ')}</div>
                  </div>

                  <div className="card kpi">
                    <div className="l">Votos apurados nas zonas</div>
                    <div className="v">{fmtN(totalVotosZonas)}</div>
                    {/* O eleitorado apto por zona está em `eleg.detalhe_zona`, que
                        nenhuma rota expõe: o KPI conta votos apurados, que é o que
                        a consulta traz, e o rótulo diz exatamente isso. */}
                    <div className="d">soma dos votos do cargo nas zonas do escopo</div>
                  </div>

                  <div className="card kpi">
                    <div className="l">{nomeMandato || 'Mandato'} · posição média</div>
                    <div className="v" style={{ color: 'var(--good)' }}>
                      {mediaPos === null ? 'sem registro' : `${fmt1(mediaPos)}º`}
                    </div>
                    <div className="d">
                      {mediaPos === null || !maisForte
                        ? 'Nenhum candidato com o nome do mandato na apuração deste cargo'
                        : `${fmtN(votosMandatoZonas)} votos · melhor: ${maisForte.pos}º em ${posicoesMandato.length} zona(s)`}
                    </div>
                  </div>

                  <div className="card kpi">
                    <div className="l">Quem lidera mais zonas</div>
                    <div className="v">{topLider ? topLider[0] : '—'}</div>
                    <div className="d">
                      {topLider
                        ? `${topLider[1]} de ${zonas.length} zonas · ${cargoDef.label}`
                        : 'sem apuração'}
                    </div>
                  </div>
                </div>

                {/* Cartões por zona, com expansão (proto:1826) */}
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))',
                    gap: 12,
                    marginTop: 12,
                  }}
                >
                  {zonas.map((z) => {
                    const aberta = zonaAberta === z.zona;
                    const lista = aberta ? z.candidatos : z.candidatos.slice(0, 5);
                    return (
                      <div
                        key={z.zona}
                        className="card flat"
                        style={{
                          cursor: 'pointer',
                          ...(aberta ? { borderColor: 'var(--eleg)' } : {}),
                        }}
                        onClick={() => setZonaAberta(aberta ? null : z.zona)}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                          }}
                        >
                          <h3 style={{ margin: 0 }}>{`Zona ${z.zona}`}</h3>
                          <span className="mono" style={{ fontSize: 11, color: 'var(--ink3)' }}>
                            {fmtN(z.total)} votos
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--ink3)', margin: '2px 0 6px' }}>
                          {z.candidatos.length} candidato(s) apurado(s) · {cargoDef.label}
                        </div>

                        <div className="tw">
                          <table>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Candidato</th>
                                <th>Votos</th>
                                <th>%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lista.map((c) => (
                                <tr
                                  key={c.id}
                                  style={
                                    semAcento(c.nome) === semAcento(nomeMandato)
                                      ? { background: 'var(--good-soft)' }
                                      : {}
                                  }
                                >
                                  <td className="num">{c.pos}</td>
                                  <td>
                                    <b>{c.nome}</b>
                                  </td>
                                  <td className="num">{fmtN(c.votos)}</td>
                                  <td className="num">{`${fmt1(c.pct)}%`}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {z.candidatos.length > 5 && (
                          <div style={{ fontSize: 11, color: 'var(--eleg)', marginTop: 4 }}>
                            {aberta ? '▴ menos' : '▾ ver todos os candidatos'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Comparativo candidato × zona (proto:1827) */}
                <div className="card flat tw" style={{ marginTop: 12, overflowX: 'auto' }}>
                  <h3>Comparativo · {cargoDef.label} · votos por zona</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Candidato</th>
                        {zonas.map((z) => (
                          <th key={z.zona} className="num">
                            {z.zona}
                          </th>
                        ))}
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nomesComparativo.map((nome) => (
                        <tr
                          key={nome}
                          style={
                            semAcento(nome) === semAcento(nomeMandato)
                              ? { background: 'var(--good-soft)' }
                              : {}
                          }
                        >
                          <td>
                            <b>{nome}</b>
                          </td>
                          {zonas.map((z) => {
                            const c = z.candidatos.find((x) => x.nome === nome);
                            return (
                              <td key={z.zona} className="num">
                                {c ? (
                                  <>
                                    {fmtN(c.votos)}
                                    {c.pos <= 3 && (
                                      <span
                                        className={`chip ${c.pos === 1 ? 'good' : 'neutral'}`}
                                        style={{ fontSize: 10, padding: '0 5px', marginLeft: 4 }}
                                      >
                                        {`${c.pos}º`}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  '—'
                                )}
                              </td>
                            );
                          })}
                          <td className="num">
                            <b>{fmtN(totaisPorCand.get(nome) || 0)}</b>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Leitura estratégica (proto:1828) */}
                <div className="note" style={{ marginTop: 12 }}>
                  <b>Leitura estratégica. </b>
                  {mediaPos !== null && maisForte && maisFraca ? (
                    <>
                      {`${nomeMandato} é mais forte na Zona ${maisForte.zona} (${maisForte.pos}º) e mais fraca na Zona ${maisFraca.zona} (${maisFraca.pos}º).`}{' '}
                      {topLider &&
                        `Onde ${topLider[0]} lidera, o eleitorado já tem dono: priorize as zonas em que a folga do 1º lugar é menor.`}
                    </>
                  ) : (
                    <>
                      {`A apuração deste cargo não traz candidato com o nome do mandato (${nomeMandato || 'mandato sem nome cadastrado'}), então não há posição própria a comparar.`}{' '}
                      {topLider && `${topLider[0]} lidera ${topLider[1]} de ${zonas.length} zonas.`}
                    </>
                  )}
                  <br />
                  <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                    Fonte: TSE · apuração por zona/seção. O candidato é identificado por nome,
                    porque a carga não guarda CPF: homônimos no mesmo município se confundem.
                  </small>
                </div>
              </>
            )}
          </div>

          {/* Votos em aberto: cadastro do gabinete, não uma lista escrita no código */}
          <div className="card" style={{ borderColor: 'var(--warn)' }}>
            <h2 style={{ color: '#B26A00' }}>Votos em aberto · ciclo {cargoDef.ciclo}</h2>
            <p style={{ color: 'var(--ink3)', fontSize: 13, margin: '-4px 0 12px' }}>
              Candidatos que o gabinete registrou como fora da disputa do mesmo cargo. São
              registros do próprio mandato na tela Brancos e nulos (tipo <b>desistente</b>), não uma
              apuração do TSE nem uma leitura automática de notícias.
            </p>

            {saidas.length === 0 ? (
              <div className="empty">
                Nenhum candidato registrado como fora da disputa neste ciclo. A plataforma não
                descobre saídas sozinha: não há ingestão de notícias nem de registro de
                candidatura. Quem acompanha a disputa registra em Brancos e nulos.
                <div style={{ marginTop: 8 }}>
                  <Link className="btn sm" to="/brancos-nulos">
                    Abrir Brancos e nulos
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="grid g4" style={{ marginBottom: 14 }}>
                  <div className="card kpi">
                    <div className="l">Votos em aberto</div>
                    <div className="v" style={{ color: 'var(--warn)' }}>
                      {fmtN(votosEmAberto)}
                    </div>
                    <div className="d">{saidas.length} registro(s) no ciclo</div>
                  </div>
                  <div className="card kpi">
                    <div className="l">Maior bolsão</div>
                    <div className="v">{saidas[0]?.candidato || 'sem nome registrado'}</div>
                    <div className="d">{fmtN(saidas[0]?.votos || 0)} votos</div>
                  </div>
                </div>

                <div className="tw">
                  <table>
                    <thead>
                      <tr>
                        <th>Candidato</th>
                        <th>Território</th>
                        <th>Votos</th>
                        <th>Observação do gabinete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {saidas.map((o) => (
                        <tr key={o.id} className="row">
                          <td>
                            <b>{o.candidato || 'sem nome registrado'}</b>
                          </td>
                          <td>{o.bairro}</td>
                          <td className="num">
                            <b>{fmtN(o.votos)}</b>
                          </td>
                          <td style={{ fontSize: 11, color: 'var(--ink3)' }}>
                            {o.observacao || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ABA 3: MÍDIAS E SENTIMENTO — lacuna declarada.
          Esta aba exibia menções contadas, percentual de sentimento positivo,
          tema dominante, manchetes com veículo e data e um "saldo de sentimento
          líquido em p.p.". Nada disso vinha de lugar nenhum: eram literais no
          código deste arquivo.

          Não há ingestão de menções no backend. Nenhuma entidade, rota,
          worker ou carga grava menção, veículo, data de publicação, alcance ou
          sentimento — o que mais perto chega é `oto.fonte`, um cadastro manual
          de fontes de conhecimento (pesquisa, notícia, discurso) sem volume,
          data de publicação nem sentimento, que o gabinete digita.

          `FalandoPage.tsx` já dizia, na própria tela, que "a plataforma não faz
          escuta de mídias sociais". Duas telas do mesmo sistema não podiam
          afirmar coisas opostas sobre a mesma capacidade: a que exibia números
          inventados é a que cede. */}
      {activeTab === 'midias' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h2>Mídias e sentimento · {scopeLabel}</h2>

            {/*
              A declaração fica visível: uma aba de mídias sem ela sugeriria que
              a plataforma escuta redes e imprensa. O detalhe de método vai para
              o ícone.
            */}
            <p style={{ fontSize: 13, lineHeight: 1.7, marginTop: 8 }}>
              A plataforma não faz escuta de mídias sociais nem de imprensa: não existe ingestão
              de menções no backend.{' '}
              <Info titulo="O que o backend não grava, e o que existe no lugar">
                Nenhuma tabela, rota ou rotina de carga grava menção, veículo, data de publicação,
                alcance ou sentimento. O que existe é um cadastro manual de fontes de conhecimento
                no OTO (pesquisa, notícia, relatório, discurso), que o gabinete digita, sem volume
                de menções, sem data de publicação e sem sentimento. É registro de leitura, não
                escuta.
              </Info>
            </p>
          </div>

          <div className="grid g21">
            <div className="card">
              <h2>O que o mandato mede da voz do território</h2>
              <p style={{ fontSize: 13, color: 'var(--ink2)', marginTop: 6 }}>
                O que se sabe do que as pessoas dizem vem do atendimento do gabinete, não de
                fora dele: as solicitações registradas em {currentBairro.n}.
              </p>

              <dl className="dl" style={{ marginTop: 10 }}>
                <dt>Solicitações registradas aqui</dt>
                <dd className="num">{sol.length}</dd>
                <dt>Menções captadas de mídias</dt>
                <dd className="num">
                  sem ingestão
                  <small style={{ display: 'block', fontSize: 11, color: 'var(--ink3)' }}>
                    não é zero: é a ausência do mecanismo que contaria
                  </small>
                </dd>
                <dt>Sentimento classificado</dt>
                <dd className="num">
                  sem classificador
                  <small style={{ display: 'block', fontSize: 11, color: 'var(--ink3)' }}>
                    nenhum modelo de sentimento roda nesta plataforma
                  </small>
                </dd>
              </dl>

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <Link className="btn pri" to="/falando">
                  O que estão falando da candidata
                </Link>
                <Link className="btn" to="/solicitacoes">
                  Ver solicitações
                </Link>
              </div>
            </div>

            <div className="card">
              <h2>
                O que faltaria para esta aba existir{' '}
                <Info titulo="Por que a aba declara a lacuna em vez de mostrar exemplo">
                  Enquanto os três não existirem, preencher a aba com exemplo faria o gabinete
                  decidir a partir de um número que ninguém mediu.
                </Info>
              </h2>
              <ol style={{ fontSize: 13, lineHeight: 1.8, paddingLeft: 18, marginTop: 8 }}>
                <li>
                  Uma coleta que grave cada menção com data, veículo, texto e território. Hoje
                  nenhuma existe.
                </li>
                <li>
                  Uma classificação de sentimento sobre esse texto, com a taxa de acerto
                  declarada junto do resultado.
                </li>
                <li>
                  Uma correspondência entre a menção e o território, para que "menções por
                  bairro" signifique alguma coisa.
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* ABA 4: VOTOS ÓRFÃOS */}
      {activeTab === 'orfaos' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="grid g4">
            <div className="card kpi">
              <div className="l">População 2026 (proj.)</div>
              <div className="v">{pop2026.toLocaleString('pt-BR')}</div>
              <div className="d">Projeção IBGE (+1,4% a.a.)</div>
            </div>

            <div className="card kpi">
              <div className="l">Eleitores 2026 (proj.)</div>
              <div className="v">{eleit2026.toLocaleString('pt-BR')}</div>
              <div className="d">Base de eleitorado estimada</div>
            </div>

            <div className="card kpi">
              <div className="l">Brancos + Nulos + Abstenção</div>
              <div className="v" style={{ color: 'var(--warn)' }}>
                {(bnPct + abstPct).toFixed(1)}%
              </div>
              <div className="d">{bnPct}% B/N · {abstPct}% Abstenção (2024)</div>
            </div>

            <div className="card kpi">
              <div className="l">Votos órfãos disponíveis</div>
              <div className="v" style={{ color: 'var(--warn)' }}>
                {votosOrfaosTotais.toLocaleString('pt-BR')}
              </div>
              <div className="d">Eleitores que não escolheram ninguém</div>
            </div>
          </div>

          <div className="card">
            <h2>Plano de conversão eleitoral</h2>
            <div className="grid g21" style={{ marginTop: 10 }}>
              <div>
                <p style={{ fontSize: 13, color: 'var(--ink2)' }}>
                  Com uma taxa conservadora de conversão de <b>12%</b> sobre os votos órfãos de {currentBairro.n}, é possível conquistar{' '}
                  <b style={{ color: 'var(--good)' }}>
                    +{Math.round(votosOrfaosTotais * 0.12).toLocaleString('pt-BR')} novos votos
                  </b>
                  , elevando a votação total para{' '}
                  <b>{(currentBairro.votos + Math.round(votosOrfaosTotais * 0.12)).toLocaleString('pt-BR')} votos</b>.
                </p>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button
                    className="btn pri"
                    onClick={() => {
                      setActiveTab('agentes');
                      handleEnviarPerguntaChat(`Qual a melhor estratégia para conquistar os votos órfãos em ${currentBairro.n}?`);
                    }}
                  >
                    Pedir estratégia ao Agente
                  </button>
                  <button
                    className="btn"
                    onClick={() => alert('Estratégia de votos órfãos salva no módulo OTO.')}
                  >
                    Salvar no OTO
                  </button>
                </div>
              </div>

              <div className="card flat" style={{ background: 'var(--surface2)' }}>
                <h3>Meta customizada de votos</h3>
                <div className="field" style={{ marginTop: 8 }}>
                  <label>Definir meta para 2028</label>
                  <input
                    type="number"
                    className="in"
                    value={metaVotos}
                    onChange={(e) => setMetaVotos(Number(e.target.value))}
                  />
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 8 }}>
                  Cobertura necessária de órfãos: {((metaVotos / (votosOrfaosTotais || 1)) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ABA 5: CHAT COM AGENTES */}
      {activeTab === 'agentes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Seletor dos 5 Estrategistas */}
          <div className="filters">
            {['O Cartógrafo', 'O Tático', 'O Sentinela', 'A Voz', 'O Maestro'].map((ag) => (
              <span
                key={ag}
                className={`chip eleg chipbtn ${estrategistaAtivo === ag ? 'on' : ''}`}
                onClick={() => setEstrategistaAtivo(ag)}
              >
                {ag}
              </span>
            ))}
          </div>

          <div className="grid g21">
            <div className="card" style={{ display: 'flex', flexDirection: 'column', minHeight: 420 }}>
              <h2>Diálogo com {estrategistaAtivo} · {scopeLabel}</h2>

              <div className="msgbox" style={{ flex: 1, maxHeight: 360, overflowY: 'auto', marginBottom: 12 }}>
                {chatMessages.map((m, idx) => (
                  <div key={idx} style={{ marginBottom: 14 }}>
                    <div className="msg me" style={{ marginBottom: 6 }}>
                      {m.q}
                    </div>
                    <div className="msg" style={{ maxWidth: '95%', background: 'var(--eleg-soft)' }}>
                      <b style={{ color: 'var(--eleg)' }}>{m.ag}</b>
                      <div style={{ marginTop: 4 }}>
                        <b>Fato.</b> {m.r.f}
                        <br />
                        <b>Cálculo.</b> {m.r.c}
                        <br />
                        <b>Interpretação.</b> {m.r.i}
                        <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
                          Fontes: {m.r.fontes}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  className="in"
                  placeholder={`Pergunte ao ${estrategistaAtivo} sobre ${currentBairro.n}...`}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleEnviarPerguntaChat()}
                />
                <button className="btn pri" onClick={() => handleEnviarPerguntaChat()}>
                  Perguntar
                </button>
              </div>
            </div>

            <div className="card">
              <h2>Perguntas sugeridas</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {[
                  `O que ${currentBairro.n} mais precisa?`,
                  `Como chegar à meta de votos em ${currentBairro.n}?`,
                  `Quais lideranças e contatos tenho em ${currentBairro.n}?`,
                  `O que estão falando nas mídias sobre o bairro?`,
                  `Quais riscos merecem atenção imediata?`,
                ].map((sug, i) => (
                  <button
                    key={i}
                    className="btn sm"
                    style={{ textAlign: 'left', padding: '8px 12px' }}
                    onClick={() => handleEnviarPerguntaChat(sug)}
                  >
                    {sug}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nova Ação */}
      <Drawer
        aberto={modalNovaAcao}
        titulo={`Planejar Ação · ${currentBairro.n}`}
        aoFechar={() => setModalNovaAcao(false)}
        rodape={
          <>
            <button className="btn" onClick={() => setModalNovaAcao(false)}>
              Cancelar
            </button>
            <button className="btn pri" onClick={handleSalvarNovaAcao}>
              Criar Ação
            </button>
          </>
        }
      >
        <div className="field" style={{ marginBottom: 12 }}>
          <label>Título da Ação *</label>
          <input
            type="text"
            className="in"
            placeholder="Ex.: Reforma e ampliação da quadra esportiva"
            value={novaAcaoTitulo}
            onChange={(e) => setNovaAcaoTitulo(e.target.value)}
          />
        </div>

        <div className="grid g2" style={{ marginBottom: 12 }}>
          <div className="field">
            <label>Tipo</label>
            <select
              className="in"
              value={novaAcaoTipo}
              onChange={(e) => setNovaAcaoTipo(e.target.value)}
            >
              <option>Obra</option>
              <option>Emenda</option>
              <option>Projeto de lei</option>
              <option>Indicação</option>
              <option>Evento</option>
              <option>Visita</option>
              <option>Ofício</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="acao-tema">Tema</label>
            {/* Catálogo do gabinete (`useTemas`), não uma lista desta tela: os
                seis nomes que estavam aqui não tinham UM literal em comum com
                as categorias reais, então toda ação criada por este drawer
                nascia com um tema que nenhuma solicitação usava. */}
            <select
              id="acao-tema"
              className="in"
              value={temaAcao}
              onChange={(e) => setNovaAcaoTema(e.target.value)}
              disabled={temas.length === 0}
            >
              {temas.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {temas.length === 0 && (
              <small style={{ fontSize: 11, color: 'var(--ink3)' }}>
                Nenhuma categoria de solicitação cadastrada. Configure em Configurações › Tipos.
              </small>
            )}
          </div>
        </div>

        <p style={{ fontSize: 11, color: 'var(--ink3)' }}>
          Sem campo de alcance: a plataforma não mede quantas pessoas uma ação atinge.{' '}
          <Info titulo="Onde registrar o alcance de uma ação">
            Quem tiver esse número o registra em Ações, onde o campo existe e o rótulo já diz que
            não é alcance verificado.
          </Info>
        </p>
      </Drawer>

      {modalEditarTerr && (
        <ModalTerritorio bairro={currentBairro} onFechar={() => setModalEditarTerr(false)} />
      )}

      {modalNovoTerr && <ModalTerritorio onFechar={() => setModalNovoTerr(false)} />}
    </div>
  );
}
